import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

export interface GoalData {
  id: number;
  year: number;
  scope: "team" | "personal";
  teamId: number | null;
  userId: number | null;
  salesGoal: string;
  commissionGoal: string;
  /** Omitted by the API for everyone except Agency Owners. */
  netCommissionGoal?: string;
  tripsTraveledGoal: number;
  createdAt: string;
  updatedAt: string;
}

export interface GoalsResponse {
  personal: GoalData | null;
  team: GoalData | null;
  canEditTeamGoals: boolean;
  /** AR-314: Agency Owners only — the net goal is hidden for everyone else. */
  canEditNetGoal: boolean;
  showTeamByDefault: boolean;
}

export interface UpsertGoalPayload {
  year: number;
  salesGoal?: string;
  commissionGoal?: string;
  netCommissionGoal?: string;
  tripsTraveledGoal?: number;
}

export function useGoals(year: number) {
  return useQuery<GoalsResponse>({
    queryKey: ["/api/goals", { year }],
    queryFn: async () => {
      const res = await fetch(`/api/goals?year=${year}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch goals");
      }
      return res.json();
    },
    staleTime: 30_000,
  });
}

export function useUpsertPersonalGoal() {
  const queryClient = useQueryClient();
  return useMutation<GoalData, Error, UpsertGoalPayload>({
    mutationFn: (data) => apiRequest("PUT", "/api/goals/personal", data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", { year: variables.year }] });
    },
  });
}

export function useUpsertTeamGoal() {
  const queryClient = useQueryClient();
  return useMutation<GoalData, Error, UpsertGoalPayload>({
    mutationFn: (data) => apiRequest("PUT", "/api/goals/team", data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", { year: variables.year }] });
    },
  });
}
